/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Box, IconButton, Text, useMediaQuery } from '@chakra-ui/react';
import { useField, useFormState } from 'react-final-form';
import { Plate, InfoMaster } from 'components';
import { MasterInfo, ServiceGroupsInfo } from 'types';
import Slider from 'react-slick';
import styled from '@emotion/styled';
import {
  AngleLeftIcon,
  AngleRightIcon,
  CheckCircleIcon,
  SwipeIcon,
} from 'static/icons';

const SLIDES_ON_DESKTOP = 3;

const IconButtonStyled = styled(IconButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.masterSelectControls};
  padding: 8px;
  margin: 0 8px;
`;

const CheckCircleIconStyled = styled(CheckCircleIcon)`
  circle {
    fill: #70c274;
  }
  display: inline;
  margin-left: 8px;
  vertical-align: text-top;
`;

export const MasterSelect: React.FC<{
  name: string;
  masters: MasterInfo[];
  possibleMastersIds?: string[];
  possibleMastersByDayIds?: string[];
  serviceGroups: ServiceGroupsInfo;
  widgetId?: string;
  isPossibleMastersExist: boolean;
  setPossibleMastersIds?: (ids?: string[]) => void;
  specialistCases: {
    specialistNominativeCase: string;
    specialistGenitiveCase: string;
    specialistPrepositionalCase: string;
  };
  serviceSelected: boolean;
}> = ({
  name,
  masters,
  possibleMastersIds,
  possibleMastersByDayIds,
  widgetId,
  isPossibleMastersExist,
  setPossibleMastersIds,
  specialistCases,
  serviceSelected,
}) => {
  const [isMobileDevice] = useMediaQuery('(max-width: 768px)');
  const { specialistNominativeCase } = specialistCases;
  const {
    values: { favorIds },
  } = useFormState<{ favorIds?: string[] }>();

  const { input } = useField(name);

  const possibleMastersByDay =
    !favorIds && possibleMastersByDayIds
      ? masters.filter((master) => possibleMastersByDayIds.includes(master.id))
      : masters;

  const possibleMasters =
    favorIds && possibleMastersIds
      ? masters.filter((master) => possibleMastersIds.includes(master.id))
      : possibleMastersByDay;

  useEffect(() => {
    if (!favorIds) {
      setPossibleMastersIds?.();
    }
  }, [favorIds]);

  const setInputValue = (value: string) => () => {
    input.onChange(value);
    slider?.slickGoTo(0, isMobileDevice);
    if (value === 'any') {
      setShowOthers(false);
    }
  };

  const [isShowOthers, setShowOthers] = useState(false);

  const isAnyMaster = input.value === 'any';

  const currentMaster =
    input.value && !isAnyMaster && masters.find(({ id }) => id === input.value);

  const anyMastersLength = possibleMasters.filter(
    (master) => master.anyEmployee,
  ).length;

  const visibleMasters = possibleMasters.filter(
    (master) => master.isShow !== false,
  );

  const otherMasters = isAnyMaster
    ? visibleMasters
    : visibleMasters.filter((master) => master.id !== input.value);

  const isShowAnyMaster =
    serviceSelected && isPossibleMastersExist && anyMastersLength > 0;

  const slidesNumber =
    // кнопка "текущий мастер"
    +!!currentMaster +
    // кнопка "любой мастер"
    +isShowAnyMaster +
    // остальные мастера
    +isShowOthers * otherMasters.length +
    // кнопка "Все доступные"
    +!isShowOthers * +!!otherMasters.length;

  const slidesToShow = Math.min(slidesNumber, SLIDES_ON_DESKTOP);

  const [slider, setSlider] = React.useState<Slider | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const isShowSliderButtons =
    !isMobileDevice && slidesNumber > SLIDES_ON_DESKTOP;

  const sliderSettings = {
    arrows: false,
    dots: false,
    infinite: false,
    slidesToShow: isMobileDevice ? 1 : slidesToShow,
    slidesToScroll: isMobileDevice ? 1 : slidesToShow,
    swipe: isMobileDevice,
    beforeChange: (_: number, next: number) => {
      setActiveSlide(next);
    },
  };

  return (
    <Box position="relative">
      {isMobileDevice && (
        <>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderWidth="1px"
            height={12}
            p={2}
            mx={1}
            my={2}
          >
            <SwipeIcon />
            <Text textStyle="body2" ml={2} color="black">
              Смахните, чтобы посмотреть больше
            </Text>
          </Box>
        </>
      )}
      {isShowSliderButtons && (
        <>
          <IconButtonStyled
            aria-label="left-arrow"
            variant="link"
            position="absolute"
            left={-12}
            top="42%"
            transform="translate(0%, -50%)"
            zIndex={2}
            onClick={() => slider?.slickPrev()}
            isDisabled={activeSlide === 0}
          >
            <AngleLeftIcon />
          </IconButtonStyled>
          <IconButtonStyled
            aria-label="right-arrow"
            variant="link"
            position="absolute"
            right={-12}
            top="42%"
            transform="translate(0%, -50%)"
            zIndex={2}
            onClick={() => slider?.slickNext()}
            isDisabled={activeSlide + slidesToShow === slidesNumber}
          >
            <AngleRightIcon />
          </IconButtonStyled>
        </>
      )}
      <Slider ref={(ref) => setSlider(ref)} {...sliderSettings}>
        {currentMaster && (
          <Box px={1}>
            <Plate
              borderRadius="xl"
              justifyContent="center"
              mb={4}
              p={2}
              background="selectedMaster"
              _hover="none"
              cursor="default"
            >
              <InfoMaster {...currentMaster} widgetId={widgetId} selected />
            </Plate>
          </Box>
        )}
        {isShowAnyMaster && (
          <Box px={1}>
            <Plate
              borderRadius="xl"
              alignContent="center"
              justifyContent="center"
              mb={4}
              p={2}
              background={isAnyMaster ? 'selectedMaster' : 'white'}
              _hover={isAnyMaster ? 'none' : { bgColor: 'hover' }}
              cursor={isAnyMaster ? 'default' : 'pointer'}
              onClick={isAnyMaster ? undefined : setInputValue('any')}
            >
              <Text
                color="black"
                textStyle="h4"
                alignContent="center"
                height={16}
                whiteSpace="nowrap"
              >
                <span>{`Любой ${specialistNominativeCase}`}</span>
                {isAnyMaster && (
                  <CheckCircleIconStyled width={18} height={18} />
                )}
              </Text>
            </Plate>
          </Box>
        )}
        {otherMasters.length > 0 &&
          (isShowOthers ? (
            otherMasters.map((master) => (
              <Box px={1} key={master.id}>
                <Plate
                  borderRadius="xl"
                  justifyContent="center"
                  mb={4}
                  p={2}
                  onClick={setInputValue(master.id)}
                  _hover={isMobileDevice ? 'none' : { bgColor: 'hover' }}
                >
                  <InfoMaster {...master} widgetId={widgetId} />
                </Plate>
              </Box>
            ))
          ) : (
            <Box px={1}>
              <Plate
                borderRadius="xl"
                alignContent="center"
                justifyContent="center"
                mb={4}
                p={2}
                onClick={() => setShowOthers(true)}
              >
                <Text
                  color="black"
                  textStyle="h4"
                  alignContent="center"
                  height={16}
                >
                  Все доступные
                </Text>
              </Plate>
            </Box>
          ))}
      </Slider>
    </Box>
  );
};
